import querystring from 'querystring';
import axios, { type AxiosResponse } from 'axios';
import { type Endpoints } from '../constants/endpoints';
import { type Requester } from './index';
import { type UsersyncStatisticsParams, type UsersyncStatisticsResponse } from '../types/usersyncStatistics';

type QueryValue = string | number | undefined;

const toQueryValue = (val?: string | number | number[] | string[]): QueryValue => {
  if (val === undefined) return undefined;
  if (Array.isArray(val)) return val.join(',');
  return val;
};

const buildQueryParams = (params: UsersyncStatisticsParams): Record<string, string | number> => {
  const queryParams: Record<string, string | number> = {
    from: params.from,
    to: params.to,
  };

  const fields: Array<keyof UsersyncStatisticsParams> = [
    'format',
    'groupBy',
    'orderBy',
    'orderDirection',
    'sspId',
    'dspId',
    'stage',
    'status',
    'reason',
    'tagType',
  ];

  fields.forEach((field) => {
    const value = toQueryValue(params[field]);
    if (value !== undefined) {
      queryParams[field] = value;
    }
  });

  if (typeof params.limit === 'number') {
    queryParams.limit = params.limit;
  }

  if (typeof params.offset === 'number') {
    queryParams.offset = params.offset;
  }

  return queryParams;
};

const extractFilename = (contentDispositionHeader?: string): string | undefined => {
  if (!contentDispositionHeader) return undefined;

  const utf8Filename = contentDispositionHeader.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Filename?.[1]) {
    try {
      return decodeURIComponent(utf8Filename[1]);
    } catch (error) {
      return undefined;
    }
  }

  const regularFilename = contentDispositionHeader.match(/filename="?([^";]+)"?/i);
  if (regularFilename?.[1]) {
    return regularFilename[1];
  }

  return undefined;
};

interface ExportUsersyncStatisticsResponse {
  blob: Blob;
  filename?: string;
}

export function createUsersyncStatistics({ authorizedGET }: Requester, endpoints: Endpoints) {
  const getUsersyncStatistics = async (params: UsersyncStatisticsParams): Promise<UsersyncStatisticsResponse> => {
    const url = `${endpoints.usersyncStatistics.root}?${querystring.stringify(buildQueryParams(params))}`;
    return authorizedGET<UsersyncStatisticsResponse>(url);
  };

  const exportUsersyncStatistics = async (
    params: UsersyncStatisticsParams
  ): Promise<ExportUsersyncStatisticsResponse> => {
    const url = `${endpoints.usersyncStatistics.export}?${querystring.stringify(buildQueryParams(params))}`;

    const headers: Record<string, string> = {};
    const token = window.localStorage.getItem('accessToken');
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response: AxiosResponse<Blob> = await axios.get(url, {
      headers,
      responseType: 'blob',
    });

    return {
      blob: response.data,
      filename: extractFilename(response.headers['content-disposition']),
    };
  };

  return {
    getUsersyncStatistics,
    exportUsersyncStatistics,
  };
}
